import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { 
  Settings, 
  CheckCircle, 
  XCircle, 
  Play, 
  RefreshCw,
  FileText,
  AlertCircle,
  Zap,
  Mail
} from 'lucide-react';

const ConfigWebhooks = () => {
  const { API, user } = useContext(AuthContext);
  const [webhooks, setWebhooks] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [saving, setSaving] = useState(null);
  const [testing, setTesting] = useState(null);
  const [activeTab, setActiveTab] = useState('config');

  useEffect(() => {
    if (user?.rol === 'admin') {
      loadWebhooks();
    }
  }, [user]);

  const loadWebhooks = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/admin/webhooks/config`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setWebhooks(data.webhooks || []);
      } else {
        toast.error('Error al cargar webhooks');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    try {
      setLoadingLogs(true);
      const response = await fetch(`${API}/admin/webhooks/logs?limit=100`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleToggleWebhook = async (webhook) => {
    setSaving(webhook.event_type);
    try {
      const response = await fetch(`${API}/admin/webhooks/config/${webhook.event_type}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          url: webhook.url,
          activo: !webhook.activo
        })
      });
      
      if (response.ok) {
        toast.success(`Webhook ${!webhook.activo ? 'activado' : 'desactivado'}`);
        loadWebhooks();
      } else {
        toast.error('Error al actualizar');
      }
    } catch (error) {
      toast.error('Error al actualizar');
    } finally {
      setSaving(null);
    }
  };

  const handleTest = async (eventType) => {
    setTesting(eventType);
    try {
      const response = await fetch(`${API}/admin/webhooks/test/${eventType}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`✅ Email de prueba enviado a soporteibauruguay@gmail.com`);
      } else {
        toast.error(`❌ Error: ${data.error || 'Fallo en el envío'}`);
      }
    } catch (error) {
      toast.error('Error al probar');
    } finally {
      setTesting(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Nunca';
    return new Date(dateString).toLocaleString('es-ES', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (user?.rol !== 'admin') {
    return (
      <div className="p-4">
        <Card className="max-w-md mx-auto">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-red-600 mb-2">Acceso Denegado</h3>
            <p className="text-gray-600">Solo administradores pueden configurar webhooks.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="bg-orange-100 p-2 rounded-lg">
            <Zap className="h-6 w-6 text-orange-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Automatizaciones / Zapier</h2>
            <p className="text-gray-600">Conecta IBA Connect con Zapier via Gmail (100% GRATIS)</p>
          </div>
        </div>
      </div>

      {/* Info Card - Nuevo método por Email */}
      <Card className="mb-6 border-l-4 border-l-green-500 bg-green-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Mail className="h-5 w-5 text-green-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">¿Cómo funciona? (Método Gmail - GRATIS)</h3>
              <ol className="text-sm text-gray-600 list-decimal list-inside space-y-1">
                <li>Los eventos activos envían un email a <strong>soporteibauruguay@gmail.com</strong></li>
                <li>En Zapier, crea un Zap con trigger <strong>"Gmail - New Email"</strong> (gratis)</li>
                <li>Filtra por asunto que contenga <strong>[ZAPIER-IBA]</strong></li>
                <li>Configura la acción: Zoho Cliq, WhatsApp, Slack, etc.</li>
              </ol>
              <div className="mt-3 p-2 bg-white rounded border border-green-200">
                <p className="text-xs text-gray-500">
                  <strong>Ejemplo de asunto:</strong> [ZAPIER-IBA] 🎓 Nuevo Estudiante Registrado
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="config" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Eventos
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center gap-2" onClick={loadLogs}>
            <FileText className="h-4 w-4" />
            Historial
          </TabsTrigger>
        </TabsList>

        {/* Tab: Configuración */}
        <TabsContent value="config">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse bg-gray-200 h-24 rounded-lg"></div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {webhooks.map((webhook) => (
                <Card 
                  key={webhook.event_type} 
                  className={`transition-all ${webhook.activo !== false ? 'border-green-200 bg-green-50/50' : 'border-gray-200'}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-800">{webhook.nombre}</h3>
                          {webhook.activo ? (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Activo
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="bg-gray-100 text-gray-600">
                              Inactivo
                            </Badge>
                          )}
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-2">{webhook.descripcion}</p>
                        
                        <div className="flex flex-wrap gap-1 mb-2">
                          {webhook.datos?.map((dato) => (
                            <span key={dato} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                              {dato}
                            </span>
                          ))}
                        </div>
                        
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Mail className="h-3 w-3" />
                          <span>Envía a: soporteibauruguay@gmail.com</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleTest(webhook.event_type)}
                          disabled={testing === webhook.event_type}
                          className="text-blue-600 border-blue-200 hover:bg-blue-50"
                          title="Enviar email de prueba"
                        >
                          {testing === webhook.event_type ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Play className="h-4 w-4 mr-1" />
                              Probar
                            </>
                          )}
                        </Button>
                        
                        <Switch
                          checked={webhook.activo !== false}
                          onCheckedChange={() => handleToggleWebhook(webhook)}
                          disabled={saving === webhook.event_type}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Tab: Historial */}
        <TabsContent value="logs">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Historial de Webhooks Enviados</CardTitle>
                <Button variant="outline" size="sm" onClick={loadLogs} disabled={loadingLogs}>
                  <RefreshCw className={`h-4 w-4 mr-1 ${loadingLogs ? 'animate-spin' : ''}`} />
                  Actualizar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingLogs ? (
                <div className="text-center py-8 text-gray-500">
                  <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                  Cargando...
                </div>
              ) : logs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No hay registros de webhooks enviados</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Fecha</th>
                        <th className="px-3 py-2 text-left">Evento</th>
                        <th className="px-3 py-2 text-center">Estado</th>
                        <th className="px-3 py-2 text-left">Detalles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-600">
                            {formatDate(log.timestamp)}
                          </td>
                          <td className="px-3 py-2 font-medium">
                            {log.event_type}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {log.success ? (
                              <Badge className="bg-green-100 text-green-700">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                {log.status_code}
                              </Badge>
                            ) : (
                              <Badge variant="destructive">
                                <XCircle className="h-3 w-3 mr-1" />
                                Error
                              </Badge>
                            )}
                          </td>
                          <td className="px-3 py-2 text-gray-500 text-xs truncate max-w-xs">
                            {log.error || log.url}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ConfigWebhooks;